import type { ReactNode } from "react";
import type { TFunction } from "i18next";

import { fmtRelative } from "~/shared/lib/fmt";
import { cn } from "~/shared/lib/utils";
import { SECTION_V5 } from "~/shared/section";
import { Badge } from "~/shared/ui/badge";
import type { Asset } from "../types";
import { AssetThumb } from "./asset-media";
import { useAssetOpenProps } from "./use-asset-open";

interface AssetCardProps {
    t: TFunction;
    asset: Asset;
    /** Optional action rendered top-right over the poster (e.g. add-to-folder); clicks don't open the chat. */
    actionSlot?: ReactNode;
    /** Click on the media poster → open the asset in the lightbox (the rest of the card → source chat). */
    onOpenMedia?: (asset: Asset) => void;
}

/** Card view for the /assets library: media poster + type badge + project/model/mode/date meta. */
export function AssetCard({ t, asset, actionSlot, onOpenMedia }: AssetCardProps) {
    const title = asset.projectName?.trim() ? asset.projectName : t("assets:card.untitled");
    // Click/keyboard on the card body opens the source chat where the media was generated
    // (no-op when unknown); the media poster opens the lightbox instead (stops propagation).
    const { openable, ...openProps } = useAssetOpenProps(asset);

    return (
        <div {...openProps} className={cn(SECTION_V5.card, openable && "cursor-pointer")}>
            <div className={SECTION_V5.cardMedia}>
                <div
                    role="presentation"
                    className={cn("absolute inset-0", onOpenMedia && "cursor-zoom-in")}
                    onClick={
                        onOpenMedia
                            ? (e) => {
                                  e.stopPropagation();
                                  onOpenMedia(asset);
                              }
                            : undefined
                    }
                >
                    <AssetThumb asset={asset} />
                </div>
                <div className="absolute top-2 left-2 flex items-center gap-1.5">
                    <Badge variant="secondary" className="bg-black/60 font-normal text-white backdrop-blur">
                        {t(`assets:type.${asset.type}`)}
                    </Badge>
                </div>
                {actionSlot ? (
                    <div
                        role="presentation"
                        className="absolute top-2 right-2"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {actionSlot}
                    </div>
                ) : null}
            </div>

            <div className="flex shrink-0 flex-col gap-2 px-4 py-3">
                <div className="flex items-start gap-2">
                    <h3 className="line-clamp-2 flex-1 text-sm leading-tight font-medium">{title}</h3>
                    <span className="shrink-0 text-[11px] whitespace-nowrap text-muted-foreground tabular-nums">
                        {fmtRelative(asset.createdAt)}
                    </span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    <Badge variant="outline" className="max-w-full truncate font-normal">
                        {asset.modelName}
                    </Badge>
                    <Badge variant="outline" className="font-normal">
                        {t(`assets:mode.${asset.mode}`)}
                    </Badge>
                </div>

                {asset.sceneName ? (
                    <span className="line-clamp-1 text-xs text-muted-foreground">{asset.sceneName}</span>
                ) : null}
            </div>
        </div>
    );
}
